if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.form.addEventListener('change', (e) => {
          if (
            e.target.name === 'id' ||
            e.target.name === 'quantity' ||
            (e.target.name && e.target.name.startsWith('options['))
          ) {
            this.updateBonusStoreCreditProps?.();
          }
        });

        // plus/minus buttons update qty via click; run after they mutate the input
        this.form.addEventListener('click', (e) => {
          if (e.target.closest('button[name="plus"], button[name="minus"]')) {
            setTimeout(() => this.updateBonusStoreCreditProps?.(), 0);
          }
        });

        // initial paint
        this.updateBonusStoreCreditProps?.();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        const activeElement = document.activeElement;
        const activeVariantSelector = activeElement?.closest?.('variant-selects');
        if (activeVariantSelector && activeElement?.name?.startsWith?.('options[')) {
          evt.preventDefault();
          return;
        }

        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        if (!this.variantIdInput.value && urlVariant) this.variantIdInput.value = urlVariant;

        // Update bonus props based on the variant we're actually submitting
        this.updateBonusStoreCreditProps?.();
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      updateBonusStoreCreditProps() {
        // These exist only on products tagged bonus-gift-card
        const displayInput = this.form.querySelector('.js-bonus-gift-card-display');
        const centsInput   = this.form.querySelector('.js-bonus-gift-card-cents');
        const mapEl        = this.form.querySelector('[data-variant-bonus-cents]');
        if (!displayInput || !centsInput || !mapEl) return;

        let bonusMap = {};
        try { bonusMap = JSON.parse(mapEl.textContent || '{}'); } catch(e) {}

        // Prefer the URL variant (your picker normalizes URL), fallback to form id
        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        const vid = String(this.variantIdInput.value || urlVariant || '');

        const perUnitCents = parseInt(bonusMap[vid], 10) || 0;

        const qtyVal =
          (this.form.elements.quantity && this.form.elements.quantity.value) ||
          (this.form.querySelector('input[name="quantity"]')?.value) ||
          1;

        const qty = Math.max(1, Number(qtyVal || 1));
        const totalCents = perUnitCents * qty;
        const hasBonus = totalCents > 0;

        // If no bonus, don't submit either property at all
        displayInput.disabled = !hasBonus;
        centsInput.disabled   = !hasBonus;

        if (!hasBonus) {
          displayInput.value = '';
          centsInput.value = '';
          return;
        }

        centsInput.value = String(totalCents);

        const moneyFormat =
          this.dataset.moneyFormat ||
          (window.Shopify && Shopify.money_format) ||
          '${{amount}}';

        displayInput.value =
          window.Shopify && typeof Shopify.formatMoney === 'function'
            ? Shopify.formatMoney(totalCents, moneyFormat)
            : `$${(totalCents / 100).toFixed(2)}`;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

document.addEventListener('click', (e) => {
  const link = e.target.closest('.js-go-nutrition');
  if (!link) return;           // only run on our special link
  e.preventDefault();

  // 1) find the Nutrition Facts label (case-insensitive substring match)
  const labels = Array.from(document.querySelectorAll('.tabs-content .tab-label'));
  const nutritionLabel = labels.find(lbl =>
    lbl.textContent.trim().toLowerCase().includes('nutrition')
  );
  if (!nutritionLabel) {
    console.warn('Nutrition-Facts label not found');
    return;
  }

  // 2) click its radio input so the tab actually opens
  const radioId = nutritionLabel.getAttribute('for');
  const radio = document.getElementById(radioId);
  if (radio) {
    radio.click();
  } else {
    console.warn('Radio not found for', radioId);
  }

  // 3) after CSS has flipped the panel to display:block, scroll it into view
  const contentId = radioId.replace(/^tab-/, 'content-');
  const panel = document.getElementById(contentId);
  if (panel) {
    // give the browser a moment to apply the :checked → display:block
    setTimeout(() => {
      const OFFSET = 150; // bump this up or down to taste
      const panelTop = panel.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: panelTop - OFFSET,
        behavior: 'smooth'
      });
    }, 150);
  } else {
    console.warn('Panel not found for', contentId);
  }
});
